import type { Comment } from "../api/types";

/**
 * comment-version-state — the PURE derivation behind the comment card's three
 * visually distinct states and its `vX` version ribbon.
 *
 * Everything here is a total function over plain data: no React, no DOM, no
 * fetch. The card is then a thin renderer of these answers, and the rules
 * ("when is a note obsolete?", "which version does the switch go to?") are
 * testable without mounting anything.
 *
 * ── THE TWO INDEPENDENT AXES (they are routinely confused) ──────────────────
 *
 * A comment carries TWO different "the document moved" signals, and they answer
 * different questions:
 *
 *   1. ANCHOR STATE (`anchor_state`: exact | shifted | repaired | orphaned |
 *      unanchored) — "did the TEXT I am pinned to move or vanish?" The first
 *      four are written at commit time by `docz.anchor.rebase` against `diffz`.
 *      The fifth is disjoint from them: `unanchored` means there is NO anchor
 *      row at all — the quote resolved to no carved section, so there is nothing
 *      to grade and no claim to make. It is about the QUOTE.
 *
 *   2. VERSION BINDING (`version_id` → `el.doc_versions.id`) — "which DOCUMENT
 *      VERSION was I written against?" Stamped server-side at create time. It is
 *      about the CITATION.
 *
 * They correlate but neither implies the other, and this module is about (2):
 *   · a `repaired` note is still anchored to the CURRENT version — its text
 *     moved, it is not obsolete;
 *   · an `orphaned` note is one whose quote stopped resolving, so its anchor
 *     stops advancing — it TENDS toward obsolete as the head moves on, but it is
 *     the version binding, not the orphaning, that makes it so;
 *   · a perfectly `exact` note written against v3 while the head is v7 IS
 *     obsolete — nothing about its anchor is wrong, it is simply a citation into
 *     an older draft.
 *
 * So "obsolete" is derived from the version binding ALONE. The anchor badge
 * ("moved" / "detached") keeps its own, separate rendering — the card shows both
 * because they tell the reader two different things.
 *
 * ── WHY THE VERSION ID NEEDS NO TRANSLATION ─────────────────────────────────
 *
 * `Comment.version_id` is an `el.doc_versions.id`. The workbench timeline builds
 * its ticks straight off the same table (`resourceVersions` →
 * `listVersions(document_id)` → `versionId: String(v.id)`). So the comment's
 * version resolves against a tick list the client ALREADY holds, and the id it
 * yields is exactly what the timeline's own `onViewVersion(kind, versionId)`
 * expects. There is one version vocabulary, not two, and the "Switch" button
 * reuses the timeline's switch rather than reimplementing it.
 *
 * The human label (`v3`) is the tick's `label`, which the tick list derives as a
 * 1-based ordinal. The client never invents its own numbering.
 */

/** The subset of `VersionTick` this module needs. Declared structurally rather
 *  than importing `@norkron/web-review-workbench` so the derivation stays
 *  dependency-free and trivially constructible in a test. Any real `VersionTick`
 *  satisfies it. */
export interface VersionTickLike {
  readonly versionId: string;
  readonly seq: number;
  readonly label: string;
  readonly isHead: boolean;
}

/** A comment's resolved document version — the tick it is bound to. */
export interface ResolvedCommentVersion {
  readonly versionId: string;
  readonly seq: number;
  /** The human label the ribbon renders, e.g. `v3`. Straight off the tick. */
  readonly label: string;
  readonly isHead: boolean;
}

/**
 * The comment card's THREE mutually exclusive presentation states. Exhaustive
 * and ordered by precedence, so a card is never ambiguous:
 *
 *   · `resolved`      — the thread is settled. Washed out; the reader's eye
 *                       should skip it. Takes precedence over obsolescence: a
 *                       resolved note needs no action regardless of which
 *                       version it cites.
 *   · `open-obsolete` — still open, but written against an EARLIER version than
 *                       the one on screen. Needs attention AND context: its
 *                       quote may not exist in what you are reading.
 *   · `open-current`  — open, written against what you are looking at. The
 *                       normal, full-strength card.
 *
 * ⛔ These are never signalled by colour or opacity ALONE (that fails
 * colour-blind and low-vision readers). Each carries a redundant non-colour
 * signal: `resolved` already has its "Resolved" text + check glyph in the status
 * pill; `open-obsolete` gets the literal `vX` ribbon text plus a history glyph.
 */
export type CommentCardState = "open-current" | "open-obsolete" | "resolved";

/**
 * Resolve the tick a comment is bound to, or `null` when it has no usable
 * binding.
 *
 * `null` is returned — and the card then behaves exactly as it always has, with
 * no ribbon and no obsolescence — whenever:
 *   · the comment carries no `version_id` (every legacy comment, and every
 *     comment created before the binding shipped); or
 *   · the bound version is not in `ticks` (a version the viewer cannot see, a
 *     tick list that has not loaded yet, or a resource mismatch).
 *
 * That direction is deliberate and load-bearing: an UNKNOWN version must never
 * be able to make a live note look obsolete, fade it, or hijack a click on its
 * quote into a switch dialogue. Missing data degrades to "normal note", never to
 * "suspicious note".
 */
export function resolveCommentVersion(
  versionId: number | null | undefined,
  ticks: readonly VersionTickLike[],
): ResolvedCommentVersion | null {
  if (versionId == null) return null;
  const key = String(versionId);
  const tick = ticks.find((t) => t.versionId === key);
  if (!tick) return null;
  return {
    versionId: tick.versionId,
    seq: tick.seq,
    label: tick.label,
    isHead: tick.isHead,
  };
}

/**
 * Which version is CURRENTLY ON SCREEN — the yardstick every comment is measured
 * against.
 *
 * `displayedVersionId` is the pane's "viewing a past version" pin (`null` when
 * the pane shows the live head). When it is null, or names a tick that is not in
 * the list, the head tick is the answer. Returns `null` only when there are no
 * ticks at all, in which case nothing can be obsolete because there is no
 * timeline to be behind.
 */
export function displayedVersion(
  displayedVersionId: string | null | undefined,
  ticks: readonly VersionTickLike[],
): ResolvedCommentVersion | null {
  if (ticks.length === 0) return null;
  const pinned =
    displayedVersionId != null
      ? ticks.find((t) => t.versionId === displayedVersionId)
      : undefined;
  const tick = pinned ?? ticks.find((t) => t.isHead) ?? ticks[ticks.length - 1];
  if (!tick) return null;
  return {
    versionId: tick.versionId,
    seq: tick.seq,
    label: tick.label,
    isHead: tick.isHead,
  };
}

/**
 * Does this comment cite a version OTHER than the one on screen? That is the
 * condition for showing the `vX` ribbon and for the quote click offering a
 * version switch.
 *
 * Note it is `!==`, not `<`: if you are reading v3 and a note was written against
 * v7, that note still cites a version you are not looking at, and offering to go
 * there is still the right affordance. Only the WORD "obsolete" (and the faded
 * treatment that goes with it) is reserved for the backwards direction — see
 * `commentCardState`.
 */
export function citesOtherVersion(
  bound: ResolvedCommentVersion | null,
  displayed: ResolvedCommentVersion | null,
): boolean {
  if (!bound || !displayed) return false;
  return bound.versionId !== displayed.versionId;
}

/**
 * The card's presentation state.
 *
 * Precedence — resolved FIRST. A resolved note is done; whether it happens to
 * cite an older draft is no longer the reader's problem, and showing it at
 * attention-grabbing "obsolete" weight would fight the whole point of washing
 * resolved threads out. (The ribbon still renders on a resolved obsolete note —
 * the FACT is still true and still useful; it is only the card's overall weight
 * that resolution decides.)
 *
 * `open-obsolete` requires the binding to be STRICTLY EARLIER (`seq <`) than what
 * is on screen. A note written against a LATER version — which you only see while
 * deliberately reading an older one — is not "obsolete"; it is from the future,
 * and calling it stale would be a lie.
 */
export function commentCardState(
  comment: Pick<Comment, "status">,
  bound: ResolvedCommentVersion | null,
  displayed: ResolvedCommentVersion | null,
): CommentCardState {
  if (comment.status === "resolved") return "resolved";
  if (bound && displayed && bound.seq < displayed.seq) return "open-obsolete";
  return "open-current";
}

/** Everything the card needs about a comment's version, in one call. */
export interface CommentVersionView {
  /** The tick the note is bound to, or null (no binding / unresolvable). */
  readonly bound: ResolvedCommentVersion | null;
  /** The version currently on screen. */
  readonly displayed: ResolvedCommentVersion | null;
  /** Render the `vX` ribbon? True iff the note cites a different version. */
  readonly showRibbon: boolean;
  /** Clicking the quote should offer a version switch rather than jump-to-anchor. */
  readonly offerSwitch: boolean;
  /** The card's presentation state. */
  readonly state: CommentCardState;
}

/**
 * The single entry point the card uses. Composes the four functions above so a
 * component never has to remember the precedence rules — and so the whole
 * decision is one assertable value in a test.
 */
export function commentVersionView(
  comment: Pick<Comment, "status" | "version_id">,
  ticks: readonly VersionTickLike[],
  displayedVersionId: string | null | undefined,
): CommentVersionView {
  const bound = resolveCommentVersion(comment.version_id, ticks);
  const displayed = displayedVersion(displayedVersionId, ticks);
  const other = citesOtherVersion(bound, displayed);
  return {
    bound,
    displayed,
    showRibbon: other,
    offerSwitch: other,
    state: commentCardState(comment, bound, displayed),
  };
}
